import {
    Flex,
    HStack,
    VStack,
    Box,
    IconButton,
    Spinner,
    Text,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@cad-arc';
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '~/api-client';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { debounce } from 'lodash';
import { ContactListItem, ContactListPanelState } from './types';
import { CategoryFilterPopover } from './category-filter-popover';
import { matchSorter } from 'match-sorter';
import { ElasticSearchResource } from '@mark43/cad-api';

export function ContactListLeftPane(props: {
    panelState: ContactListPanelState;
    setPanelState: React.Dispatch<ContactListPanelState>;
    contacts: ContactListItem[];
    setContacts: React.Dispatch<ContactListItem[]>;
}): React.ReactElement {
    const enabledFeatures = useEnabledFeatures();
    const apiClient = useApiClient();
    const [selectedFilters, setSelectedFilters] = React.useState<number[]>([]);
    const [searchText, setSearchText] = React.useState<string>('');
    const [queryText, setQueryText] = React.useState<string>('');
    const debouncedSetQueryText = React.useMemo(() => {
        return debounce(setQueryText, 100);
    }, [setQueryText]);
    React.useEffect(() => {
        debouncedSetQueryText(searchText);
    }, [searchText, debouncedSetQueryText]);
    const queryEnabled = queryText.length > 2 || selectedFilters.length > 0;
    const query = useQuery({
        queryKey: ['contact-list-search', queryText, selectedFilters],
        queryFn: () => {
            return apiClient
                .request<ElasticSearchResource.QuickSearchRolodexProfiles>({
                    method: 'POST',
                    url: 'elastic_search/rolodex_profiles/quick_search/',
                    data: {
                        // @ts-expect-error some properties that aren't actually required appear
                        // required in the types
                        elasticQuery: {
                            query: queryText,
                            rolodexProfileTypeAttrIds: selectedFilters,
                        },
                    },
                })
                .then((response) => {
                    return response.data.data;
                });
        },
        onSuccess(data) {
            props.setContacts(
                data?.map((rolodexProfile) => {
                    const primaryRolodexContact = rolodexProfile.rolodexContacts.find(
                        (contact) => contact.id === rolodexProfile.primaryRolodexContactId
                    );

                    return {
                        id: rolodexProfile.id,
                        orgName: rolodexProfile.name,
                        primaryContactName: primaryRolodexContact?.name,
                        primaryContactPhoneNumber: primaryRolodexContact?.phoneNumber,
                    };
                }) ?? []
            );
        },
        enabled: queryEnabled,
    });
    return (
        <VStack h="100%" w="270px" bgColor="surface.foreground" alignItems="flex-start" flexShrink={0}
            borderRight="sm"
            borderColor="border.default">
            <HStack justifyContent="space-between" alignItems="center" w="100%" p={2}  borderBottom="sm" borderColor="border.default">
                <Input
                    aria-label="Search"
                    placeholder="Search"
                    leftIcon="Search"
                    autoFocus
                    value={searchText}
                    onChange={(e) => {
                        setSearchText(e.target.value);
                    }}
                />
                <Popover>
                    <PopoverTrigger asChild>
                        <IconButton
                            icon="Filter"
                            isActive={selectedFilters.length > 0}
                            aria-label="filter by category popover"
                            variant="ghost"
                        />
                    </PopoverTrigger>
                    <PopoverContent>
                        <CategoryFilterPopover
                            selectedFilters={selectedFilters}
                            setSelectedFilters={setSelectedFilters}
                        />
                    </PopoverContent>
                </Popover>
            </HStack>
            <VStack flexGrow={100} w="100%" overflowY="auto" spacing={0} alignItems="stretch">
                {!queryEnabled && !props.contacts.length ? (
                    <Flex w="100%" justifyContent="center">
                        <Text variant="caption">Search or pick categories to view contacts</Text>
                    </Flex>
                ) : query.isInitialLoading ? (
                    <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
                        <Spinner />
                    </Flex>
                ) : (
                    matchSorter(props.contacts, queryText, {
                        keys: ['orgName'],
                        baseSort: (a, b) => a.item.orgName.localeCompare(b.item.orgName),
                        threshold: matchSorter.rankings.NO_MATCH,
                    }).map((contact, index) => (
                        <Box
                            p={2}
                            cursor="pointer"
                            key={contact.id}
                            data-testid="contact-list-search-result"
                            borderTop={index !== 0 ? "sm" : ""}
                            borderColor="border.default"
                            backgroundColor={
                                props.panelState.selectedContactId === contact.id
                                    ? 'interactive.hover'
                                    : undefined
                            }
                            onClick={() => {
                                props.setPanelState({
                                    selectedContactId: contact.id,
                                    formMode: 'VIEW',
                                });
                            }}
                        >
                            <VStack alignItems="flex-start" overflow="hidden" wordBreak="break-all">
                                <Text variant="headingXs" noOfLines={1}>
                                    {contact.orgName}
                                </Text>
                                <HStack w="100%" justifyContent="space-between">
                                    <Text variant="caption" noOfLines={1}>
                                        {contact.primaryContactName}
                                    </Text>
                                    <Text variant="caption" noOfLines={0} style={{ width: 85 }}>
                                        {contact.primaryContactPhoneNumber}
                                    </Text>
                                </HStack>
                            </VStack>
                        </Box>
                    ))
                )}
            </VStack>
            {enabledFeatures.editContactList ? (
                <Box p={2} width="full" borderTop="sm" borderColor="border.default">
                    <IconButton
                        variant="solid"
                        icon="Add"
                        aria-label="add contact"
                        onClick={() => {
                            props.setPanelState({
                                formMode: 'CREATE',
                                selectedContactId: undefined,
                            });
                        }}
                    />
                </Box>
            ) : undefined}
        </VStack>
    );
}
